import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { MatDialog } from '@angular/material/dialog';
import { ITelefono } from './interfaces/telefono.interface';
import { TelefonoService } from './services/telefono.service';
import { OpcionesService } from './services/opciones.service';
import { IOpcion } from './interfaces/opciones.interface';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-telefono-form',
  templateUrl: './telefono-form.component.html',
  providers: [MessageService]
})
export class TelefonoFormComponent implements OnInit, OnChanges {
  @Input() model: ITelefono | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;
  tipoTelefonoOpciones: IOpcion[] = [];
  boletinadoOpciones: IOpcion[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private telefonoService: TelefonoService,
    private opcionesService: OpcionesService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadOpciones();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadTelefono();
      } else {
        this.initForm();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      telefono: [{ value: '', disabled: !!this.model }, [Validators.pattern(/^\d{10}$/)]],
      primer_nombre: [''],
      segundo_nombre: [''],
      apellido_paterno: [''],
      apellido_materno: [''],
      calle: [''],
      nombre_empresa: [''],
      numero_ext: [''],
      boletinado: [''],
      tipoTelefono: ['']
    });
  }

  loadOpciones(): void {
    // Cargar opciones de tipo de teléfono
    this.opcionesService.getTipoTelefono().subscribe({
      next: (response) => {
        if (response.success) {
          this.tipoTelefonoOpciones = response.data;
        }
      },
      error: (error) => {
        console.error('Error al cargar opciones de tipo de teléfono:', error);
      }
    });

    // Cargar opciones de boletinado
    this.opcionesService.getBoletinado().subscribe({
      next: (response) => {
        if (response.success) {
          this.boletinadoOpciones = response.data;
        }
      },
      error: (error) => {
        console.error('Error al cargar opciones de boletinado:', error);
      }
    });
  }

  loadTelefono(): void {
    if (!this.model?.telefono) return;
    
    this.loading = true;
    this.telefonoService.getTelefonoByNumero(this.model.telefono).subscribe({
      next: (response) => {
        if (response.success && response.data.length > 0) {
          const telefono = response.data[0];
          this.form.patchValue({
            telefono: telefono.telefono,
            primer_nombre: telefono.primer_nombre,
            segundo_nombre: telefono.segundo_nombre,
            apellido_paterno: telefono.apellido_paterno,
            apellido_materno: telefono.apellido_materno,
            calle: telefono.calle,
            nombre_empresa: telefono.nombre_empresa,
            numero_ext: telefono.numero_ext,
            boletinado: telefono.boletinado,
            tipoTelefono: telefono.tipoTelefono
          });
          this.form.get('telefono')?.disable();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo cargar el teléfono'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar el teléfono'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor verifique los datos ingresados'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 1,
      tope: '',
      telefono: formData.telefono,
      primer_nombre: formData.primer_nombre,
      segundo_nombre: formData.segundo_nombre,
      apellido_paterno: formData.apellido_paterno,
      apellido_materno: formData.apellido_materno,
      calle: formData.calle,
      nombre_empresa: formData.nombre_empresa,
      numero_ext: formData.numero_ext,
      boletinado: formData.boletinado,
      tipoTelefono: formData.tipoTelefono
    };

    this.telefonoService.saveTelefono(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Teléfono creado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear el teléfono'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear el teléfono'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor verifique los datos ingresados'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 2,
      tope: '',
      telefono: formData.telefono,
      primer_nombre: formData.primer_nombre,
      segundo_nombre: formData.segundo_nombre,
      apellido_paterno: formData.apellido_paterno,
      apellido_materno: formData.apellido_materno,
      calle: formData.calle,
      nombre_empresa: formData.nombre_empresa,
      numero_ext: formData.numero_ext,
      boletinado: formData.boletinado,
      tipoTelefono: formData.tipoTelefono
    };

    this.telefonoService.updateTelefono(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Teléfono actualizado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el teléfono'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el teléfono'
        });
        this.loading = false;
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'telefon',
        sClave: `${this.form.get('telefono')?.value}`,
        sNombre: this.form.get('primer_nombre')?.value,
        sTipo: 'Opcion'
      }
    });
  }
} 